"use client";

import { createRecordsGateway } from "@/interface-adapters/records/services/RecordsGateway";
import {
  AdminRecordListItemDTO,
  PaginatedRecordsResponseDTO,
  RecordListItemDTO,
  UpdateRecordsRequestDTO,
} from "@/application/records/dtos/RecordListItemDTO";
import { getAuthHeaders } from "@/infrastructure/http/authHeaders";

const recordsGateway = createRecordsGateway();

export type RecordListItem = RecordListItemDTO;
export type AdminRecordListItem = AdminRecordListItemDTO;
export type PaginatedRecordsResponse = PaginatedRecordsResponseDTO;

export interface ChangeName {
  id: string;
  name: string;
}

export type UpdateRecordsRequest = UpdateRecordsRequestDTO;

function toFacadeError(context: string, error: unknown): Error {
  if (error instanceof Error) {
    return new Error(`${context}: ${error.message}`);
  }
  return new Error(`${context}: Unknown error`);
}

async function withFacadeError<T>(
  context: string,
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw toFacadeError(context, error);
  }
}

export function getBaseUrl(): string {
  const base = process.env.NEXT_PUBLIC_CUSTDEV_API_URL;
  if (!base) {
    throw new Error("NEXT_PUBLIC_CUSTDEV_API_URL is not set");
  }
  return base.replace(/\/$/, "");
}

export async function getAuthHeader(): Promise<Record<string, string>> {
  return getAuthHeaders();
}

export function clearRecordsCaches(): void {
  // Legacy API exposed cache invalidation; logic now delegated to React Query.
}

export async function getRecordsListOptimized(): Promise<RecordListItem[]> {
  return withFacadeError("Failed to get optimized records list", () =>
    recordsGateway.getRecordsList()
  );
}

export async function getRecordsList(): Promise<RecordListItem[]> {
  return withFacadeError("Failed to get records list", () =>
    recordsGateway.getRecordsList()
  );
}

export async function getAdminRecordsListOptimized(): Promise<
  AdminRecordListItem[]
> {
  return withFacadeError("Failed to get optimized admin records list", () =>
    recordsGateway.getAdminRecordsList()
  );
}

export async function getAdminRecordsList(): Promise<AdminRecordListItem[]> {
  return withFacadeError("Failed to get admin records list", () =>
    recordsGateway.getAdminRecordsList()
  );
}

export async function getRecordsPaginated(
  page: number = 1,
  limit: number = 10
): Promise<PaginatedRecordsResponse> {
  return withFacadeError("Failed to get paginated records", () =>
    recordsGateway.getRecordsPaginated(page, limit)
  );
}

export async function updateRecords(
  request: UpdateRecordsRequest
): Promise<void> {
  await withFacadeError("Failed to update records", () =>
    recordsGateway.updateRecords(request)
  );
}

export async function patchTranscriptUtterance(
  recordId: string,
  utteranceId: number,
  newText: string,
  version?: string
): Promise<void> {
  await withFacadeError("Failed to patch transcript utterance", () =>
    recordsGateway.patchTranscriptUtterance(
      recordId,
      utteranceId,
      newText,
      version ? Number(version) : undefined
    )
  );
}

export async function getMarkdownQAReport(
  recordId: string,
  version?: string | number | null
) {
  return withFacadeError("Failed to fetch QA markdown report", () =>
    recordsGateway.getMarkdownReport(recordId, "qa-report", { version })
  );
}

export async function updateMarkdownQAReport(
  recordId: string,
  markdownContent: string,
  version?: number | null
): Promise<void> {
  await withFacadeError("Failed to update QA markdown report", () =>
    recordsGateway.updateMarkdownReport(
      recordId,
      "qa-report",
      markdownContent,
      version
    )
  );
}

export async function getMarkdownPersonaReport(
  recordId: string,
  version?: string | number | null
) {
  return withFacadeError("Failed to fetch Persona markdown report", () =>
    recordsGateway.getMarkdownReport(recordId, "persona-report", {
      version,
    })
  );
}

export async function updateMarkdownPersonaReport(
  recordId: string,
  markdownContent: string,
  version?: number | null
): Promise<void> {
  await withFacadeError("Failed to update Persona markdown report", () =>
    recordsGateway.updateMarkdownReport(
      recordId,
      "persona-report",
      markdownContent,
      version
    )
  );
}

export async function getMarkdownCustomReport(
  recordId: string,
  version?: string | number | null
) {
  return withFacadeError("Failed to fetch Custom markdown report", () =>
    recordsGateway.getMarkdownReport(recordId, "custom-report", {
      version,
    })
  );
}

export async function updateMarkdownCustomReport(
  recordId: string,
  markdownContent: string,
  version?: number | null
): Promise<void> {
  await withFacadeError("Failed to update Custom markdown report", () =>
    recordsGateway.updateMarkdownReport(
      recordId,
      "custom-report",
      markdownContent,
      version
    )
  );
}

export async function getMarkdownJTBDReport(
  recordId: string,
  version?: string | number | null
) {
  return withFacadeError("Failed to fetch JTBD markdown report", () =>
    recordsGateway.getMarkdownReport(recordId, "jtbd-markdown-report", {
      version,
    })
  );
}

export async function updateMarkdownJTBDReport(
  recordId: string,
  markdownContent: string,
  version?: number | null
): Promise<void> {
  await withFacadeError("Failed to update JTBD markdown report", () =>
    recordsGateway.updateMarkdownReport(
      recordId,
      "jtbd-markdown-report",
      markdownContent,
      version
    )
  );
}

export async function switchPublicity(
  recordId: string,
  isPublic: boolean
): Promise<void> {
  await withFacadeError("Failed to switch record publicity", () =>
    recordsGateway.switchPublicity(recordId, isPublic)
  );
}
